import React from 'react';
import { Radio, RadioGroup, FormControlLabel, makeStyles, createStyles } from '@material-ui/core';
import { StandardInputWrapper } from '@components/StatefulInputs/Base/StandardInputWrapper/StandardInputWrapper';
import { FieldValues, useController } from 'react-hook-form';
import { StatefulRadioProps } from '@components/StatefulInputs/StatefulRadio/StatefulRadio.types';

const useStyles = makeStyles((theme) => createStyles({
	radioGroup: {
		'display': 'flex',
		'flexDirection': 'row',
		'gap': theme.spacing(3),
		'marginLeft': theme.spacing(1),
		'color': theme.palette.grey[700],
		'backgroundColor': theme.palette.background.paper,
		'& .MuiFormControlLabel-root .MuiRadio-root.Mui-checked': {
			color: theme.palette.primary.main,
		},
	},
	errorRadio: {
		'&.Mui-checked': {
			color: theme.palette.error.main,
		},
		'color': theme.palette.error.main,
	},
	errorLabel: {
		'color': theme.palette.error.main,
		'&.Mui-error': {
			color: theme.palette.error.main,
		},
	},
	checkedRadio: {
		'&.Mui-checked': {
			color: theme.palette.primary.main,
		},
	},
}));


export function StatefulRadio<FormType extends FieldValues>({
	controllerOptions,
	options,
	radioGroupProps,
	radioProps,
	...statefulProviderProps
}: StatefulRadioProps<FormType>) {
	const classes = useStyles();
	const { field, fieldState } = useController({ name: statefulProviderProps.name, ...controllerOptions });

	// without all those field.value checks, an error class would flash briefly when switching options
	const hasError = fieldState.invalid && (field.value === null || field.value === undefined || field.value === '');

	// Convert field value to string for Material-UI
	const stringValue = field.value === null || field.value === undefined
		? ''
		: String(field.value);

	// Handle change and convert string back to original type
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const stringVal = event.target.value;
		const option = options.find(opt => String(opt.value) === stringVal);
		field.onChange(option?.value);
	};

	return (
		<StandardInputWrapper<FormType> {...statefulProviderProps} shouldShowError={hasError}>
			<RadioGroup
				{...radioGroupProps}
				{...field}
				value={stringValue}
				onChange={handleChange}
				className={classes.radioGroup}
			>
				{options.map((option) => (
					<FormControlLabel
						key={String(option.value)}
						value={String(option.value)}
						control={<Radio {...radioProps} className={hasError ? classes.errorRadio : classes.checkedRadio} />}
						label={option.label}
						className={hasError ? classes.errorLabel : ''}
					/>
				))}
			</RadioGroup>
		</StandardInputWrapper>
	);
}
